import React, { Component } from 'react';
import axios from 'axios';
import { translate } from 'react-i18next';
import { getCsrfToken } from '../../utils/utils';

const styles = {
  container: {
    maxWidth: 900,
    margin: '40px auto',
    padding: '0 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  h1: {
    fontSize: 24,
    marginBottom: 30,
    borderBottom: '2px solid #2196F3',
    paddingBottom: 10,
  },
  section: {
    marginBottom: 30,
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 15,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    background: '#f5f5f5',
  },
  td: {
    padding: '6px 12px',
    borderBottom: '1px solid #f0f0f0',
  },
  btn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    marginRight: 10,
  },
  btnPrimary: {
    background: '#2196F3',
    color: '#fff',
  },
  btnDanger: {
    background: '#f44336',
    color: '#fff',
  },
  btnSuccess: {
    background: '#4CAF50',
    color: '#fff',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: 4,
    marginRight: 10,
    fontSize: 14,
    minWidth: 250,
  },
  alert: {
    padding: '10px 15px',
    borderRadius: 6,
    marginTop: 10,
    fontSize: 14,
  },
  alertSuccess: {
    background: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
  },
  alertError: {
    background: '#ffebee',
    color: '#c62828',
    border: '1px solid #ef9a9a',
  },
  alertInfo: {
    background: '#e3f2fd',
    color: '#1565c0',
    border: '1px solid #90caf9',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: '#f5f5f5',
    borderRadius: 6,
    padding: '12px 16px',
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 20,
    fontWeight: 700,
    color: '#333',
  },
};


class DemoPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: null,
      loading: true,
      resetting: false,
      refreshing: false,
      sendingReport: false,
      selectedGarageId: '',
      message: null,
      messageType: null,
      confirmReset: false,
    };
  }

  componentDidMount() {
    this.fetchStatus();
  }

  fetchStatus = () => {
    this.setState({ loading: true });
    axios.get('/uiapi/1/demo/status/')
      .then(res => {
        this.setState({ status: res.data, loading: false });
      })
      .catch(() => {
        this.setState({ loading: false, message: this.props.t('notAvailable'), messageType: 'error' });
      });
  }

  handleReset = () => {
    if (!this.state.confirmReset) {
      this.setState({ confirmReset: true });
      return;
    }
    this.setState({ resetting: true, confirmReset: false, message: null });
    axios.post('/uiapi/1/demo/reset/', {}, {
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
      .then(() => {
        this.setState({ resetting: false, message: this.props.t('resetComplete'), messageType: 'success' });
        this.fetchStatus();
      })
      .catch(() => {
        this.setState({ resetting: false, message: this.props.t('resetFailed'), messageType: 'error' });
      });
  }

  handleRefresh = () => {
    this.setState({ refreshing: true, message: null });
    axios.post('/uiapi/1/demo/refresh-data/', {}, {
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
      .then(() => {
        this.setState({ refreshing: false, message: this.props.t('refreshQueued'), messageType: 'success' });
      })
      .catch(() => {
        this.setState({ refreshing: false, message: this.props.t('refreshFailed'), messageType: 'error' });
      });
  }

  handleSendReport = () => {
    const { selectedGarageId } = this.state;
    this.setState({ sendingReport: true, message: null });
    const payload = selectedGarageId ? { garage_ids: [parseInt(selectedGarageId)] } : { all: true };
    axios.post('/uiapi/1/demo/send-monthly-report/', payload, {
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
      .then(res => {
        this.setState({
          sendingReport: false,
          message: `Monthly report sent (${res.data.reports_sent} reports)`,
          messageType: 'success',
        });
      })
      .catch(() => {
        this.setState({ sendingReport: false, message: this.props.t('sendReportFailed'), messageType: 'error' });
      });
  }

  render() {
    const { status, loading, resetting, refreshing, sendingReport, selectedGarageId, message, messageType, confirmReset } = this.state;
    const { t } = this.props;

    if (loading) {
      return <div style={styles.container}><p>{t('loadingPanel')}</p></div>;
    }

    if (!status) {
      return <div style={styles.container}><p>{t('notAvailableEnvironment')}</p></div>;
    }

    return (
      <div style={styles.container}>
        <h1 style={styles.h1}>{t('pageTitle')}</h1>

        {message && (
          <div style={{
            ...styles.alert,
            ...(messageType === 'success' ? styles.alertSuccess : messageType === 'error' ? styles.alertError : styles.alertInfo),
          }}>
            {message}
          </div>
        )}

        {/* Status */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{t('statusHeading')}</h2>
          <div style={styles.statGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>{t('organizations')}</div>
              <div style={styles.statValue}>{status.organizations.length}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>{t('workshops')}</div>
              <div style={styles.statValue}>{status.workshops.length}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>{t('users')}</div>
              <div style={styles.statValue}>{status.users.length}</div>
            </div>
            {status.organizations.map(org => (
              <div key={org.id} style={styles.statCard}>
                <div style={styles.statLabel}>{org.name}</div>
                <div style={styles.statValue}>{org.vehicle_count} {t('vehicles')}</div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {org.task_count} {t('tasks')} / {org.message_count} {t('messages')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Demo Users */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{t('demoUsersHeading')}</h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{t('email')}</th>
                <th style={styles.th}>{t('password')}</th>
                <th style={styles.th}>{t('portal')}</th>
                <th style={styles.th}>{t('role')}</th>
                <th style={styles.th}>{t('name')}</th>
              </tr>
            </thead>
            <tbody>
              {status.users.map(u => (
                <tr key={u.email}>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}><code>{u.password}</code></td>
                  <td style={styles.td}>{u.portal}</td>
                  <td style={styles.td}>{u.role}</td>
                  <td style={styles.td}>{u.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{t('actionsHeading')}</h2>

          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('resetHeading')}</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
              {t('resetDescription')}
            </p>
            <button
              style={{
                ...styles.btn,
                ...styles.btnDanger,
                ...(resetting ? styles.btnDisabled : {}),
              }}
              onClick={this.handleReset}
              disabled={resetting}
            >
              {resetting ? t('resetting') : confirmReset ? t('confirmReset') : t('resetButton')}
            </button>
            {confirmReset && (
              <button
                style={{ ...styles.btn, background: '#eee', color: '#333' }}
                onClick={() => this.setState({ confirmReset: false })}
              >
                {t('cancelButton')}
              </button>
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('refreshHeading')}</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
              {t('refreshDescription')}
            </p>
            <button
              style={{
                ...styles.btn,
                ...styles.btnPrimary,
                ...(refreshing ? styles.btnDisabled : {}),
              }}
              onClick={this.handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? t('refreshing') : t('refreshButton')}
            </button>
          </div>

          <div>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('sendReportHeading')}</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
              {t('sendReportDescription')}
            </p>
            <select
              style={styles.select}
              value={selectedGarageId}
              onChange={e => this.setState({ selectedGarageId: e.target.value })}
            >
              <option value="">{t('allGarages')}</option>
              {status.garages.map(g => (
                <option key={g.id} value={g.id}>{g.name} ({g.organization})</option>
              ))}
            </select>
            <button
              style={{
                ...styles.btn,
                ...styles.btnSuccess,
                ...(sendingReport ? styles.btnDisabled : {}),
              }}
              onClick={this.handleSendReport}
              disabled={sendingReport}
            >
              {sendingReport ? t('sending') : t('sendReportButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default translate('DemoPanel')(DemoPanel);
